<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-paper-container">
      <div class="orangehrm-header-container">
        <oxd-text tag="h6" class="orangehrm-main-title">
          {{ $t('pim.run_payroll') }}
        </oxd-text>
        <div>
          <oxd-button
            :label="$t('pim.run_payroll')"
            icon-name="plus"
            display-type="secondary"
            @click="onClickAdd"
          />
        </div>
      </div>
      <table-header
        :selected="checkedItems.length"
        :total="total"
        :loading="isLoading"
        @delete="onClickDeleteSelected"
      ></table-header>
      <div class="orangehrm-container">
        <oxd-card-table
          v-model:selected="checkedItems"
          :loading="isLoading"
          :headers="headers"
          :items="items?.data"
          :selectable="true"
          :clickable="true"
          row-decorator="oxd-table-decorator-card"
        />
      </div>
      <div class="orangehrm-bottom-container">
        <oxd-pagination
          v-if="showPaginator"
          v-model:current="currentPage"
          :length="pages"
        />
      </div>
    </div>

    <delete-confirmation ref="deleteDialog"></delete-confirmation>
  </div>
</template>

<script>
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog';
import {navigate} from '@ohrm/core/util/helper/navigation';
import {APIService} from '@ohrm/core/util/services/api.service';
import {computed} from 'vue';

export default {
  components: {
    'delete-confirmation': DeleteConfirmationDialog,
  },

  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/pim/paystubs',
    );
    const {
      showPaginator,
      currentPage,
      total,
      pages,
      pageSize,
      response,
      isLoading,
      execQuery,
    } = usePaginate(http);
    // Mask SSN helper
    const maskSSN = (ssn) => {
      if (!ssn) return 'xxx-xx-xxxx';

      const digits = ssn.replace(/\D/g, '');
      const last4 = digits.slice(-4);

      return `xxx-xx-${last4 || 'xxxx'}`;
    };

    // Computed list with masked SSN
    const maskedItems = computed(() => {
      const res = response.value || {};

      const data = Array.isArray(res.data)
        ? res.data.map((item) => ({
            ...item,
            ssn: maskSSN(item.ssn),
          }))
        : [];
      return {
        ...res,
        data,
      };
    });
    return {
      http,
      showPaginator,
      currentPage,
      isLoading,
      total,
      pages,
      pageSize,
      execQuery,
      items: maskedItems,
    };
  },
  data() {
    return {
      headers: [
        {
          name: 'ssn',
          slot: 'title',
          title: this.$t('SSN'),
          style: {'flex-basis': '70%'},
        },
        {
          name: 'firstName',
          slot: 'title',
          title: this.$t('general.first_name'),
          style: {'flex-basis': '70%'},
        },
        {
          name: 'lastName',
          slot: 'title',
          title: this.$t('general.last_name'),
          style: {'flex-basis': '70%'},
        },
        {
          name: 'startDate',
          slot: 'title',
          title: this.$t('general.start_date'),
          style: {'flex-basis': '70%'},
        },
        {
          name: 'endDate',
          slot: 'title',
          title: this.$t('general.end_date'),
          style: {'flex-basis': '70%'},
        },
        {
          name: 'grossPay',
          slot: 'title',
          title: this.$t('pim.gross_pay'),
          style: {'flex-basis': '70%'},
        },
        {
          name: 'totalDeductions',
          slot: 'title',
          title: this.$t('pim.total_deductions'),
          style: {'flex-basis': '70%'},
        },
        {
          name: 'netPay',
          slot: 'title',
          title: this.$t('pim.net_pay'),
          style: {'flex-basis': '70%'},
        },
        {
          name: 'actions',
          slot: 'action',
          title: this.$t('general.actions'),
          style: {flex: 1},
          cellType: 'oxd-table-cell-actions',
          cellConfig: {
            view: {
              onClick: this.onClickDownload,
              props: {
                name: 'download',
              },
            },
            edit: {
              onClick: this.onClickEdit,
              props: {
                name: 'pencil-fill',
              },
            },
          },
        },
      ],
      checkedItems: [],
    };
  },

  methods: {
    onClickAdd() {
      this.isLoading = true;
      this.http
        .create({
          startDate: '0000-00-00',
        })
        .then(() => {
          return this.$toast.saveSuccess();
        })
        .then(() => {
          navigate('/pim/runPayroll');
        });
    },
    onClickEdit($event) {
      const id = $event.empNumber ? $event.empNumber : $event.item?.empNumber;
      //console.log($event);
      navigate('/pim/viewPersonalDetails/empNumber/{id}', {id});
    },
    getFileId(filename, empNumber) {
      const str1 = '/api/v2/pim/employees/';
      const str2 = '/screen/personal/attachments';
      const str = str1.concat(empNumber, str2);
      const sfiles = new APIService(window.appGlobal.baseUrl, str);
      let imm = null;
      sfiles.getAll().then((response) => {
        let dt = response.data;
        imm = dt;
        //console.log(imm);
      });
      return imm;
    },
    onClickDownload(item) {
      const dash = '-';
      const pdf = '.pdf';
      const fl = item.firstName.concat(item.lastName, dash, item.endDate, pdf);
      //const id = this.getFileId(fl, item.empNumber);
      const str1 = '/api/v2/pim/employees/';
      const str2 = '/screen/personal/attachments';
      const str = str1.concat(item.empNumber, str2);
      const sfiles = new APIService(window.appGlobal.baseUrl, str);
      sfiles.getAll().then((response) => {
        let dat = response.data;
        const fle = dat.data.find((f) => f.filename === fl);
        if (fle) {
          //console.log(fle);
          let id = fle.id;
          const downUrl = `${window.appGlobal.baseUrl}/pim/viewAttachment/empNumber/${item.empNumber}/attachId/${id}`;
          window.open(downUrl, '_blank');
        }
      });
    },
    async resetDataTable() {
      await this.execQuery();
    },
  },
};
</script>
